import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { emailBlastApi } from '@/api/emailBlast'
import type { EmailBlastSendingDomainPlanDto, EmailBlastViewDto } from '@/types/emailBlast'
import { getDisplayErrorMessages } from '@/utils/errorHandling'
import { useToast } from './useToast'

/**
 * Loads one blast (with engagement) for the deep-linkable detail route (ORB-384) and exposes the
 * resume/cancel controls. Mirrors useEmailBlasts' action handling — Resume surfaces the API's 409
 * (blast not Paused) distinctly — but scoped to a single blast rather than a campaign list.
 *
 * The blast's sending-domain plan (ORB-953) loads with it, in the same request pair, so the verdict never
 * combines a blast and a plan from different moments.
 */
export function useEmailBlastDetail({ workspaceId, blastId }: { workspaceId: string; blastId: string }) {
  const [blast, setBlast] = useState<EmailBlastViewDto | null>(null)
  const [plan, setPlan] = useState<EmailBlastSendingDomainPlanDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actioning, setActioning] = useState(false)
  const [resumeRefusal, setResumeRefusal] = useState<string[] | null>(null)

  const { showSuccess, showError, showErrorUnless422 } = useToast()

  // Generation token: only the latest load applies, so a superseded/unmounted load can't write stale data.
  const requestIdRef = useRef(0)

  /**
   * `quiet` refetches without touching `loading` (ORB-706). The realtime hook calls it on every engine
   * tick, and driving the shared spinner from a background refresh makes the page's Refresh control
   * strobe on its own every few minutes.
   */
  const load = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!workspaceId || !blastId) return
    const reqId = ++requestIdRef.current
    if (!quiet) setLoading(true)
    // Deliberately NOT cleared up front. A quiet refetch that gets superseded applies no result, so an
    // eager clear here would silently retract the "couldn't refresh" warning without having proved
    // anything — and that warning is the only thing telling an operator the figures went stale.
    const [blastResponse, planResponse] = await Promise.all([
      emailBlastApi.get(workspaceId, blastId),
      emailBlastApi.sendingDomainPlan(workspaceId, blastId),
    ])
    if (requestIdRef.current !== reqId) {
      // A later load superseded this one. Still clear the spinner we raised, or a quiet realtime
      // refetch landing mid-Refresh leaves the button disabled and spinning until a page reload.
      if (!quiet) setLoading(false)
      return
    }
    // Set or cleared only once this response is the one that counts. A plan that failed leaves the page
    // on the last one it had, under the same "couldn't refresh" warning as a blast that failed.
    setError(blastResponse.error ?? planResponse.error ?? null)
    if (blastResponse.data) setBlast(blastResponse.data)
    if (planResponse.data) setPlan(planResponse.data)
    if (!quiet) setLoading(false)
  }, [workspaceId, blastId])

  const refetchQuietly = useCallback(() => { void load({ quiet: true }) }, [load])

  useEffect(() => {
    void load()
    return () => { requestIdRef.current++ }
  }, [load])

  const resume = useCallback(async () => {
    setActioning(true)
    setResumeRefusal(null)
    try {
      const res = await emailBlastApi.resume(workspaceId, blastId)
      if (res.error || !res.data) {
        if (res.status === 409) {
          showError('Cannot resume', 'This blast is no longer paused. Refreshing its status.')
          await load()
        } else if (res.status === 422) {
          // Refused because the sending domain stopped after this page loaded. The reason stays beside
          // the button, and the reload brings the stopped domain onto the page.
          setResumeRefusal(getDisplayErrorMessages(res.error))
          await load({ quiet: true })
        } else {
          showErrorUnless422('Resume failed', res)
        }
        return
      }
      setBlast(res.data)
      showSuccess('Blast resumed', 'Sending has resumed.')
      // Resuming changes what the domain's plan says about this broadcast.
      void load({ quiet: true })
    } finally {
      setActioning(false)
    }
  }, [workspaceId, blastId, load, showError, showErrorUnless422, showSuccess])

  const cancel = useCallback(async () => {
    setActioning(true)
    try {
      const res = await emailBlastApi.cancel(workspaceId, blastId)
      if (res.error || !res.data) {
        showErrorUnless422('Cancel failed', res)
        return
      }
      setBlast(res.data)
      showSuccess('Blast cancelled', 'No further emails will be sent.')
      void load({ quiet: true })
    } finally {
      setActioning(false)
    }
  }, [workspaceId, blastId, load, showErrorUnless422, showSuccess])

  return { blast, plan, loading, error, actioning, resumeRefusal, load, refetchQuietly, resume, cancel }
}
